import React from 'react';
import { Container, Box, Button, Typography, Card, CardContent, Tabs, Tab, Fab, Grid } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { FAQPermissionGuard } from '../../../components/PermissionGuard';
import { FAQList } from '../components/FAQList';
import { FAQEditor } from '../components/FAQEditor';
import { AgentTester } from '../components/AgentTester';
import { FAQStats } from '../components/FAQStats';
import { useFAQStats } from '../hooks/useFAQApi';

const TabPanel: React.FC<{ children: React.ReactNode, value: number, index: number }> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

export const FAQDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState(0);
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const { data: stats, isLoading: statsLoading } = useFAQStats();

  return (
    <FAQPermissionGuard>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            🤖 Gestion FAQ WhatsApp
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gérez vos questions/réponses automatiques et testez votre agent en temps réel
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4">
                    {statsLoading ? '...' : stats?.total_faqs || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total FAQs
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4">
                    {statsLoading ? '...' : stats?.recent_faqs || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cette semaine
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4">
                    {statsLoading ? '...' : Math.round(stats?.avg_answer_length || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mots/réponse
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Actions */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateForm(true)}
          >
            Nouvelle FAQ
          </Button>
        </Box>

        {/* Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
              <Tab label="Mes FAQs" />
              <Tab label="Test Agent" />
              <Tab label="Statistiques" />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            <FAQList />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <AgentTester />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <FAQStats />
          </TabPanel>
        </Card>

        {/* FAB */}
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setShowCreateForm(true)}
        >
          <AddIcon />
        </Fab>

        {/* Modals */}
        {showCreateForm && (
          <FAQEditor
            open={showCreateForm}
            onClose={() => setShowCreateForm(false)}
            mode="create"
          />
        )}
      </Container>
    </FAQPermissionGuard>
  );
};
